import React, { useEffect, useMemo, useState } from "react";
import { useAuth, hasPermission, isProductionForeman } from "../context/AuthContext";
import { useBoilerWorkspace } from "../hooks/useBoilerWorkspace";
import RuntimeCounter, { formatElapsedClock, elapsedParts } from "../components/RuntimeCounter";
import TouchNumericKeypad from "../components/boiler/TouchNumericKeypad";
import BoilerReadingCard, {
  formatPressure,
  formatTemp,
  huskFeedCardTone,
  steamPressureCardTone,
  waterLevelCardTone,
} from "../components/boiler/BoilerReadingCard";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Flame, AlertTriangle, Clock, Droplets, Users, ScrollText } from "lucide-react";
import { Link } from "react-router-dom";
import { INDIA_LOCALE, INDIA_TIME_ZONE } from "../lib/indiaTime";
import { parseNumberInput } from "../lib/numberInput";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <span className="font-mono text-sm tabular-nums text-muted-foreground">
      {now.toLocaleString(INDIA_LOCALE, {
        timeZone: INDIA_TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })}
    </span>
  );
}

function formatDueLabel(hourlyCheck) {
  if (hourlyCheck.overdue) return "Hourly check overdue";
  const sec = hourlyCheck.due_in_seconds;
  if (sec == null) return "Start boiler to begin hourly checks";
  const mins = Math.ceil(sec / 60);
  return `Hourly check due in ${mins} min${mins === 1 ? "" : "s"}`;
}

function formatMetric(value, suffix = "") {
  if (value == null || value === "") return "—";
  return `${value}${suffix}`;
}

function formatBedHours(hours) {
  if (hours == null || Number.isNaN(hours)) return "0.0";
  return Number(hours).toFixed(1);
}

function formatDurationClock(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function formatTimeLabel(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString(INDIA_LOCALE, {
    timeZone: INDIA_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
  });
}

function displayBedHours(baseHours, running, nowMs, fetchedAtMs) {
  let hours = Number(baseHours) || 0;
  if (running && fetchedAtMs) {
    hours += Math.max(0, (nowMs - fetchedAtMs) / 3600000);
  }
  return hours;
}

export default function BoilerWorkspacePage() {
  const { user } = useAuth();
  const canControl = hasPermission(user, "production.monitoring.control");
  const {
    data,
    loading,
    submitting,
    runtime,
    readings,
    hourlyCheck,
    todayWaterTest,
    waterTestDoneToday,
    closureReasons,
    breakdownCategories,
    pressureLimits,
    lastBlowdownHoursAgo,
    bedTracking,
    operatorShift,
    blowdown,
    startBoiler,
    takeOverShift,
    endShift,
    stopBoiler,
    startBlowdown,
    stopBlowdown,
    saveHourlyLog,
    cycleWaterLevel,
    toggleHuskFeedBed,
    saveWaterTest,
    reportBreakdown,
  } = useBoilerWorkspace();

  const isRunning = runtime.status === "RUNNING";
  const operatorName = operatorShift.current_operator_name || "—";
  const canOperate = canControl && isRunning && operatorShift.is_current_operator;
  const showForemanLink = isProductionForeman(user);

  const [keypad, setKeypad] = useState(null);
  const [keypadValue, setKeypadValue] = useState("");
  const [endShiftOpen, setEndShiftOpen] = useState(false);
  const [shutdownOpen, setShutdownOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [breakdownCategory, setBreakdownCategory] = useState("");
  const [breakdownOther, setBreakdownOther] = useState("");
  const [waterForm, setWaterForm] = useState({ water_ph: "", water_hardness: "", water_tds: "" });
  const [shiftForm, setShiftForm] = useState({
    ash_discharges_count: "",
    closure_reason: "PLANNED_SHUTDOWN",
  });
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [bedFetchedAt, setBedFetchedAt] = useState(() => Date.now());

  useEffect(() => {
    setBedFetchedAt(Date.now());
  }, [
    bedTracking.bed1_active_hours,
    bedTracking.bed2_active_hours,
    bedTracking.bed1_running,
    bedTracking.bed2_running,
  ]);

  useEffect(() => {
    if (!isRunning && !blowdown.active?.is_active) return undefined;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isRunning, blowdown.active?.is_active]);

  useEffect(() => {
    if (!todayWaterTest) return;
    setWaterForm({
      water_ph: todayWaterTest.water_ph != null ? String(todayWaterTest.water_ph) : "",
      water_hardness: todayWaterTest.water_hardness != null ? String(todayWaterTest.water_hardness) : "",
      water_tds: todayWaterTest.water_tds != null ? String(todayWaterTest.water_tds) : "",
    });
  }, [todayWaterTest]);

  const openKeypad = (field, title, { allowDecimal = true, initial = "" } = {}) => {
    setKeypad({ field, title, allowDecimal });
    setKeypadValue(initial != null && initial !== "" ? String(initial) : "");
  };

  const closeKeypad = () => {
    setKeypad(null);
    setKeypadValue("");
  };

  const confirmKeypad = async () => {
    if (!keypad) return;

    if (keypad.field.startsWith("water_")) {
      setWaterForm((f) => ({ ...f, [keypad.field]: keypadValue }));
      closeKeypad();
      return;
    }

    if (!canOperate) return;
    const n = keypad.allowDecimal ? parseNumberInput(keypadValue) : parseNumberInput(keypadValue, { integer: true });
    if (n == null) return;
    const ok = await saveHourlyLog({ [keypad.field]: n });
    if (ok) closeKeypad();
  };

  const openEndShiftDrawer = () => {
    setEndShiftOpen(true);
  };

  const toggleBlowdown = async () => {
    if (blowdownActive) {
      await stopBlowdown();
    } else {
      await startBlowdown();
    }
  };

  const confirmEndShift = async () => {
    const ok = await endShift({
      ash_discharges_count: parseNumberInput(shiftForm.ash_discharges_count, { integer: true }) ?? 0,
    });
    if (ok) {
      setEndShiftOpen(false);
      setShiftForm({
        ash_discharges_count: "",
        closure_reason: "PLANNED_SHUTDOWN",
      });
    }
  };

  const confirmShutdown = async () => {
    const ok = await stopBoiler({ closure_reason: shiftForm.closure_reason });
    if (ok) {
      setShutdownOpen(false);
      setShiftForm((f) => ({ ...f, closure_reason: "PLANNED_SHUTDOWN" }));
    }
  };

  const confirmStart = async (fuelBeds) => {
    const ok = await startBoiler(fuelBeds);
    if (ok) setStartOpen(false);
  };

  const confirmBreakdown = async () => {
    if (!breakdownCategory) return;
    if (breakdownCategory === "OTHER" && !breakdownOther.trim()) return;
    const ok = await reportBreakdown({
      reason_category: breakdownCategory,
      other_remarks: breakdownCategory === "OTHER" ? breakdownOther.trim() : null,
    });
    if (ok) {
      setBreakdownOpen(false);
      setBreakdownCategory("");
      setBreakdownOther("");
    }
  };

  const submitWaterTest = async () => {
    const payload = {
      water_ph: parseNumberInput(waterForm.water_ph),
      water_hardness: parseNumberInput(waterForm.water_hardness),
      water_tds: parseNumberInput(waterForm.water_tds, { integer: true }),
    };
    if (payload.water_ph == null && payload.water_hardness == null && payload.water_tds == null) {
      toast.error("Enter at least one water test value (pH, hardness, or TDS)");
      return;
    }
    await saveWaterTest(payload);
  };

  const runtimeParts = useMemo(
    () => elapsedParts(runtime.start_time),
    [runtime.start_time, isRunning],
  );

  const bed1Hours = displayBedHours(bedTracking.bed1_active_hours, bedTracking.bed1_running, nowMs, bedFetchedAt);
  const bed2Hours = displayBedHours(bedTracking.bed2_active_hours, bedTracking.bed2_running, nowMs, bedFetchedAt);

  const blowdownActive = Boolean(blowdown.active?.is_active);
  const blowdownElapsedSeconds = blowdownActive && blowdown.active?.started_at
    ? Math.max(0, Math.floor((nowMs - new Date(blowdown.active.started_at).getTime()) / 1000))
    : blowdown.active?.elapsed_seconds ?? 0;
  const lastBlowdown = blowdown.last_completed;

  const keypadOverlayFields = new Set(["ash_count"]);

  if (loading && !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground" data-testid="boiler-workspace-loading">
        Loading boiler…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4 pb-24 sm:pb-8" data-testid="boiler-workspace-page">
      <div className="rounded-2xl border-2 border-bansal-sage/30 bg-gradient-to-br from-bansal-ivory to-white p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Flame className="h-6 w-6 text-orange-600" />
              <h1 className="font-serif text-xl sm:text-2xl text-foreground">Boiler</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{data?.boiler_name || "Thermax 10-Ton Combipack"}</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <LiveClock />
          </div>
        </div>
      </div>

      {showForemanLink && (
        <Link
          to="/production/team"
          className="flex items-center gap-2 rounded-xl border border-bansal-sage/40 bg-bansal-sageLight/40 px-4 py-3 text-sm text-primary hover:bg-bansal-sageLight"
        >
          <Users className="h-4 w-4 shrink-0" />
          My team — monitor operators, blowdown history, and manage accounts
        </Link>
      )}

      <Link
        to="/production/boiler/register"
        className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm text-foreground hover:bg-accent/60"
      >
        <ScrollText className="h-4 w-4 shrink-0 text-primary" />
        Log register — full activity history with operator and duration
      </Link>

      <section className="rounded-2xl border-2 border-border bg-card p-4 sm:p-5 space-y-4" data-testid="boiler-zone-status">
        <h2 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Live status control</h2>
        <div className="space-y-1">
          <p className="text-sm">
            Current operator: <strong>{operatorName}</strong>
          </p>
          {isRunning && operatorShift.pending_handover && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              You have an open shift. End your shift when you leave — the boiler stays running for the next operator.
            </p>
          )}
          {isRunning && operatorShift.can_take_over && !operatorShift.is_current_operator && (
            <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              Tap <strong>Take over shift</strong> when you arrive. The previous operator will end their shift when they leave.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold ${isRunning ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>
              <span className={`h-2.5 w-2.5 rounded-full ${isRunning ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
              {isRunning ? "RUNNING" : runtime.status || "STOPPED"}
            </span>
            {isRunning && (
              <RuntimeCounter
                startTime={runtime.start_time}
                running
                showVerbose={false}
                clockClassName="font-mono text-xl sm:text-2xl tabular-nums text-primary font-bold"
                testId="boiler-live-runtime"
              />
            )}
            {!isRunning && (
              <span className="font-mono text-xl tabular-nums text-muted-foreground">{formatElapsedClock({ hours: 0, minutes: 0, seconds: 0 })}</span>
            )}
          </div>
          {isRunning && (
            <p className="text-xs text-muted-foreground">
              Shift runtime: {Math.floor(runtimeParts.hours)}h {runtimeParts.minutes}m {runtimeParts.seconds}s
              {" · "}
              Bed 1 active: {formatBedHours(bed1Hours)}h
              {" · "}
              Bed 2 active: {formatBedHours(bed2Hours)}h
            </p>
          )}
        </div>

        <div className="space-y-3">
          {!isRunning ? (
            <Button
              type="button"
              disabled={!canControl || submitting}
              className="min-h-[72px] text-lg bg-emerald-600 hover:bg-emerald-700 text-white w-full"
              onClick={() => setStartOpen(true)}
              data-testid="boiler-start-btn"
            >
              Start boiler
            </Button>
          ) : (
            <>
              {operatorShift.can_take_over && (
                <Button
                  type="button"
                  disabled={!canControl || submitting}
                  className="min-h-[72px] text-lg bg-emerald-600 hover:bg-emerald-700 text-white w-full"
                  onClick={takeOverShift}
                  data-testid="boiler-takeover-btn"
                >
                  Take over shift
                </Button>
              )}

              {(operatorShift.can_end_shift || operatorShift.is_current_operator) && (
                <div className="grid grid-cols-2 gap-3">
                  {operatorShift.can_end_shift && (
                    <Button
                      type="button"
                      disabled={!canControl || submitting}
                      className="min-h-[72px] text-lg bg-bansal-sage hover:bg-bansal-sageDark text-white"
                      onClick={openEndShiftDrawer}
                      data-testid="boiler-end-shift-btn"
                    >
                      End my shift
                    </Button>
                  )}
                  {operatorShift.is_current_operator && (
                    <Button
                      type="button"
                      disabled={!canControl || submitting}
                      variant={blowdownActive ? "default" : "outline"}
                      className={cn(
                        "min-h-[72px] text-lg touch-manipulation",
                        !operatorShift.can_end_shift && "col-span-2",
                        blowdownActive
                          ? "bg-sky-600 hover:bg-sky-700 text-white border-sky-600"
                          : "border-2",
                      )}
                      onClick={toggleBlowdown}
                      data-testid="boiler-blowdown-btn"
                    >
                      <span className="block">{blowdownActive ? "Stop blowdown" : "Start blowdown"}</span>
                      {blowdownActive && (
                        <span className="block text-xs font-normal opacity-90 mt-0.5 font-mono tabular-nums">
                          {formatDurationClock(blowdownElapsedSeconds)}
                        </span>
                      )}
                      {!blowdownActive && lastBlowdown && (
                        <span className="block text-xs font-normal text-muted-foreground mt-0.5">
                          Last {formatDurationClock(lastBlowdown.duration_seconds)} · {formatTimeLabel(lastBlowdown.started_at)}–{formatTimeLabel(lastBlowdown.ended_at)}
                        </span>
                      )}
                      {!blowdownActive && !lastBlowdown && lastBlowdownHoursAgo != null && (
                        <span className="block text-xs font-normal text-muted-foreground mt-0.5">
                          Last: {lastBlowdownHoursAgo}h ago
                        </span>
                      )}
                    </Button>
                  )}
                </div>
              )}

              {operatorShift.is_current_operator && (
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    disabled={!canControl || submitting}
                    className="min-h-[72px] text-lg bg-amber-500 hover:bg-amber-600 text-white border-0"
                    onClick={() => setBreakdownOpen(true)}
                    data-testid="boiler-breakdown-btn"
                  >
                    <AlertTriangle className="h-5 w-5 mr-2 inline" />
                    Report breakdown
                  </Button>
                  <Button
                    type="button"
                    disabled={!canControl || submitting}
                    className="min-h-[72px] text-lg bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => setShutdownOpen(true)}
                    data-testid="boiler-stop-btn"
                  >
                    Stop boiler
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section className="rounded-2xl border-2 border-border bg-card p-4 sm:p-5 space-y-4" data-testid="boiler-zone-hourly">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Quick hour logs</h2>
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${hourlyCheck.overdue ? "bg-red-100 text-red-800" : "bg-amber-50 text-amber-900"}`}>
            {formatDueLabel(hourlyCheck)}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <BoilerReadingCard
            label="Steam pressure"
            display={formatPressure(readings.steam_pressure)}
            toneClass={steamPressureCardTone(readings.steam_pressure, pressureLimits)}
            disabled={!canOperate || submitting}
            onClick={() => openKeypad("steam_pressure", "Steam pressure (kg/cm²)", { allowDecimal: true, initial: readings.steam_pressure })}
            testId="boiler-card-pressure"
          />
          <BoilerReadingCard
            label="Flue gas temp"
            display={formatTemp(readings.flue_gas_temp)}
            disabled={!canOperate || submitting}
            onClick={() => openKeypad("flue_gas_temp", "Flue gas temperature (°C)", { allowDecimal: false, initial: readings.flue_gas_temp })}
            testId="boiler-card-flue"
          />
          <BoilerReadingCard
            label="Water level"
            display={readings.water_level || "NORMAL"}
            toneClass={waterLevelCardTone(readings.water_level)}
            sublabel="Tap to cycle"
            disabled={!canOperate || submitting}
            onClick={cycleWaterLevel}
            testId="boiler-card-water"
          />
          <BoilerReadingCard
            label="Economizer / feedwater temp"
            display={formatTemp(readings.economizer_temp)}
            disabled={!canOperate || submitting}
            onClick={() => openKeypad("economizer_temp", "Economizer temp (°C)", { allowDecimal: false, initial: readings.economizer_temp })}
            testId="boiler-card-economizer"
          />
          <BoilerReadingCard
            label="Husk feed bed 1"
            display={readings.husk_feed_bed1 || "STOPPED"}
            toneClass={huskFeedCardTone(readings.husk_feed_bed1)}
            sublabel={isRunning ? `Active: ${formatBedHours(bed1Hours)}h · tap to toggle` : "Tap to toggle"}
            disabled={!canOperate || submitting}
            onClick={() => toggleHuskFeedBed(1)}
            testId="boiler-card-husk-bed1"
          />
          <BoilerReadingCard
            label="Husk feed bed 2"
            display={readings.husk_feed_bed2 || "STOPPED"}
            toneClass={huskFeedCardTone(readings.husk_feed_bed2)}
            sublabel={isRunning ? `Active: ${formatBedHours(bed2Hours)}h · tap to toggle` : "Tap to toggle"}
            disabled={!canOperate || submitting}
            onClick={() => toggleHuskFeedBed(2)}
            testId="boiler-card-husk-bed2"
          />
        </div>
      </section>

      <section className="rounded-2xl border-2 border-border bg-card p-4 sm:p-5 space-y-4" data-testid="boiler-zone-water-test">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-2">
            <Droplets className="h-4 w-4" />
            Daily water quality analysis
          </h2>
          {waterTestDoneToday && (
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">Logged today</span>
          )}
        </div>

        {todayWaterTest?.logged_at && (
          <p className="text-xs text-muted-foreground">
            Last saved {formatTimeLabel(todayWaterTest.logged_at)}
            {todayWaterTest.operator_name ? ` by ${todayWaterTest.operator_name}` : ""}
          </p>
        )}

        <div className="grid grid-cols-3 gap-3">
          <BoilerReadingCard
            label="pH"
            display={formatMetric(waterForm.water_ph)}
            sublabel="Tap to edit"
            disabled={!canControl || submitting}
            onClick={() => openKeypad("water_ph", "Water pH", { allowDecimal: true, initial: waterForm.water_ph })}
            testId="boiler-card-water-ph"
          />
          <BoilerReadingCard
            label="Hardness"
            display={formatMetric(waterForm.water_hardness, " ppm")}
            sublabel="Tap to edit"
            disabled={!canControl || submitting}
            onClick={() => openKeypad("water_hardness", "Water hardness (ppm)", { allowDecimal: true, initial: waterForm.water_hardness })}
            testId="boiler-card-water-hardness"
          />
          <BoilerReadingCard
            label="TDS"
            display={formatMetric(waterForm.water_tds, " ppm")}
            sublabel="Tap to edit"
            disabled={!canControl || submitting}
            onClick={() => openKeypad("water_tds", "Water TDS (ppm)", { allowDecimal: false, initial: waterForm.water_tds })}
            testId="boiler-card-water-tds"
          />
        </div>

        <Button
          type="button"
          disabled={!canControl || submitting}
          className="w-full min-h-[56px] bg-bansal-sage hover:bg-bansal-sageDark text-white"
          onClick={submitWaterTest}
          data-testid="boiler-save-water-test"
        >
          {waterTestDoneToday ? "Update today's water test" : "Save daily water test"}
        </Button>
      </section>

      <TouchNumericKeypad
        open={Boolean(keypad) && !keypadOverlayFields.has(keypad?.field)}
        title={keypad?.title}
        value={keypadValue}
        onChange={setKeypadValue}
        onClose={closeKeypad}
        onConfirm={confirmKeypad}
        allowDecimal={keypad?.allowDecimal !== false}
        suffix={
          keypad?.field === "steam_pressure" ? "kg/cm²"
          : keypad?.field === "water_hardness" || keypad?.field === "water_tds" ? "ppm"
          : ""
        }
      />

      {endShiftOpen && (
        <div className="fixed inset-0 z-[90] flex flex-col justify-end bg-black/50" data-testid="boiler-end-shift-drawer">
          <div className="bg-card rounded-t-3xl p-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] max-h-[90vh] overflow-y-auto space-y-4">
            <h3 className="font-serif text-xl text-foreground">End my shift</h3>
            <p className="text-sm text-muted-foreground">The boiler keeps running. Confirm your shift tasks before you leave.</p>

            <div className="rounded-xl border bg-muted/30 p-3 text-sm space-y-1">
              <p>Bed 1 active this shift (auto): <strong>{formatBedHours(bed1Hours)} h</strong></p>
              <p>Bed 2 active this shift (auto): <strong>{formatBedHours(bed2Hours)} h</strong></p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Ash discharges (count)</Label>
              <Button type="button" variant="outline" className="w-full min-h-[56px] mt-2 text-lg font-mono justify-start" onClick={() => { setKeypad({ field: "ash_count", title: "Ash discharges", allowDecimal: false }); setKeypadValue(shiftForm.ash_discharges_count || ""); }}>
                {shiftForm.ash_discharges_count || "0"}
              </Button>
            </div>

            {blowdownActive && (
              <p className="text-sm text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
                Blowdown in progress ({formatDurationClock(blowdownElapsedSeconds)}). It will be stopped automatically when you end your shift.
              </p>
            )}

            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button type="button" variant="outline" className="min-h-[56px]" onClick={() => setEndShiftOpen(false)}>Cancel</Button>
              <Button type="button" className="min-h-[56px] bg-bansal-sage hover:bg-bansal-sageDark text-white" disabled={submitting} onClick={confirmEndShift} data-testid="boiler-end-shift-confirm">
                End shift
              </Button>
            </div>
          </div>
        </div>
      )}

      {shutdownOpen && (
        <div className="fixed inset-0 z-[90] flex flex-col justify-end bg-black/50" data-testid="boiler-stop-drawer">
          <div className="bg-card rounded-t-3xl p-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] max-h-[90vh] overflow-y-auto space-y-4">
            <h3 className="font-serif text-xl text-foreground">Stop boiler</h3>
            <p className="text-sm text-muted-foreground">Shut down the combipack boiler. Use this only for planned shutdown or when steam is no longer needed.</p>

            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Closure reason</Label>
              <Select value={shiftForm.closure_reason} onValueChange={(v) => setShiftForm((f) => ({ ...f, closure_reason: v }))}>
                <SelectTrigger className="min-h-[56px] mt-2 text-base"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {closureReasons.map((r) => (
                    <SelectItem key={r.value} value={r.value} className="min-h-[44px] text-base">{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button type="button" variant="outline" className="min-h-[56px]" onClick={() => setShutdownOpen(false)}>Cancel</Button>
              <Button type="button" className="min-h-[56px] bg-red-600 hover:bg-red-700 text-white" disabled={submitting} onClick={confirmShutdown} data-testid="boiler-stop-confirm">
                Stop boiler
              </Button>
            </div>
          </div>
        </div>
      )}

      {startOpen && (
        <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/60 p-3 sm:p-4" data-testid="boiler-start-modal">
          <div className="bg-card rounded-2xl p-5 w-full max-w-lg space-y-4">
            <h3 className="font-serif text-xl text-foreground">Which fuel bed are you starting?</h3>
            <p className="text-sm text-muted-foreground">Select the fluidised bed(s) firing husk for this shift.</p>

            <div className="grid grid-cols-1 gap-2">
              {[
                { value: "BED1", label: "Bed 1 only", sub: "Start husk feed on bed 1" },
                { value: "BED2", label: "Bed 2 only", sub: "Start husk feed on bed 2" },
                { value: "BOTH", label: "Both beds", sub: "Start husk feed on bed 1 and bed 2" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  disabled={submitting}
                  onClick={() => confirmStart(opt.value)}
                  className="min-h-[64px] rounded-xl border-2 border-border px-4 text-left touch-manipulation hover:bg-emerald-50 hover:border-emerald-500 active:scale-[0.98] transition-colors"
                  data-testid={`start-fuel-${opt.value.toLowerCase()}`}
                >
                  <span className="block font-semibold text-base text-foreground">{opt.label}</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">{opt.sub}</span>
                </button>
              ))}
            </div>

            <Button type="button" variant="outline" className="w-full min-h-[52px]" onClick={() => setStartOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {breakdownOpen && (
        <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/60 p-3 sm:p-4" data-testid="boiler-breakdown-modal">
          <div className="bg-card rounded-2xl p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-4">
            <h3 className="font-serif text-xl text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-6 w-6" />
              Report breakdown
            </h3>
            <p className="text-sm text-muted-foreground">Select the issue category. The boiler will stop and the reason is logged.</p>

            <div className="grid grid-cols-1 gap-2">
              {breakdownCategories.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setBreakdownCategory(cat.value)}
                  className={cn(
                    "min-h-[56px] rounded-xl border-2 px-4 text-left text-base font-medium touch-manipulation transition-colors",
                    breakdownCategory === cat.value
                      ? "border-red-500 bg-red-50 text-red-900"
                      : "border-border hover:bg-muted/40",
                  )}
                  data-testid={`breakdown-cat-${cat.value}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {breakdownCategory === "OTHER" && (
              <Textarea
                value={breakdownOther}
                onChange={(e) => setBreakdownOther(e.target.value)}
                placeholder="Describe the breakdown…"
                className="min-h-[120px] text-base"
                data-testid="breakdown-other-remarks"
              />
            )}

            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button type="button" variant="outline" className="min-h-[56px]" onClick={() => { setBreakdownOpen(false); setBreakdownCategory(""); setBreakdownOther(""); }}>
                Cancel
              </Button>
              <Button
                type="button"
                className="min-h-[56px] bg-red-600 hover:bg-red-700 text-white"
                disabled={!breakdownCategory || submitting || (breakdownCategory === "OTHER" && !breakdownOther.trim())}
                onClick={confirmBreakdown}
                data-testid="breakdown-confirm"
              >
                Confirm breakdown
              </Button>
            </div>
          </div>
        </div>
      )}

      {keypad?.field === "ash_count" && (
        <TouchNumericKeypad
          open
          title="Ash discharges"
          value={keypadValue}
          onChange={setKeypadValue}
          onClose={closeKeypad}
          allowDecimal={false}
          onConfirm={() => {
            setShiftForm((f) => ({ ...f, ash_discharges_count: keypadValue || "0" }));
            closeKeypad();
          }}
        />
      )}
    </div>
  );
}
